import {FormControl, InputLabel, MenuItem, Select, TextField, Tooltip, Typography} from '@mui/material';
import {DatePicker, LocalizationProvider} from '@mui/x-date-pickers';
import {AdapterDateFns} from '@mui/x-date-pickers/AdapterDateFnsV3';
import {ru} from 'date-fns/locale';
import {get} from 'lodash';
import {FC, useMemo} from 'react';
import {Control, Controller, useFormContext} from 'react-hook-form';
import {useGetDictsServiceTypesMyQuery} from '../../../../global/services/service-types';
import {CONTRACT_TABLE_FILTERS_STATUSES} from '../../constants/contract-table-filters.constants';
import {ContractFiltersFormValues} from '../../types';

interface FieldOptions {
  label?: string;
  value?: string;
}

interface RendererProps {
  control: Control<ContractFiltersFormValues>;
  options?: FieldOptions[];
}

const FIELD_RENDERER = {
  contractNumber: ({control}: RendererProps) => (
    <Controller
      name="contractNumber"
      control={control}
      render={({field}) => (
        <TextField {...field} label="Номер договора" type="text" fullWidth size="small" value={field.value || ''} />
      )}
    />
  ),
  companyName: ({control}: RendererProps) => (
    <Controller
      name="companyName"
      control={control}
      render={({field}) => (
        <TextField {...field} label="Заказчик" type="text" fullWidth size="small" value={field.value || ''} />
      )}
    />
  ),
  status: ({control, options}: RendererProps) => (
    <Controller
      name="status"
      control={control}
      render={({field}) => (
        <FormControl fullWidth size="small">
          <InputLabel id="status-label">Статус</InputLabel>
          <Select {...field} labelId="status-label" label="Статус">
            {options?.map((status) => (
              <MenuItem key={status.value} value={status.value}>
                <Tooltip placement="top" title={status.label}>
                  <Typography noWrap textOverflow="ellipsis" maxWidth="234px">
                    {status.label}
                  </Typography>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  ),
  counter: ({control}: RendererProps) => (
    <Controller
      name="counter"
      control={control}
      render={({field}) => (
        <TextField
          {...field}
          label="Порядковый номер договора"
          type="number"
          fullWidth
          size="small"
          value={field.value || ''}
        />
      )}
    />
  ),
  serviceType: ({control, options}: RendererProps) => (
    <Controller
      name="serviceType"
      control={control}
      render={({field}) => (
        <FormControl fullWidth size="small">
          <InputLabel id="serviceType-label">Вид работ</InputLabel>
          <Select {...field} labelId="serviceType-label" label="Вид работ">
            {options?.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                <Tooltip placement="top" title={type.label}>
                  <Typography noWrap textOverflow="ellipsis" maxWidth="234px">
                    {type.label}
                  </Typography>
                </Tooltip>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      )}
    />
  ),
  startDate: ({control}: RendererProps) => (
    <Controller
      name="startDate"
      control={control}
      render={({field: {value, onChange}}) => (
        <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
          <DatePicker
            value={value || null}
            onChange={onChange}
            label="Дата заключения"
            format="dd.MM.yyyy"
            slotProps={{popper: {disablePortal: true}}}
          />
        </LocalizationProvider>
      )}
    />
  ),
  dueDate: ({control}: RendererProps) => (
    <Controller
      name="dueDate"
      control={control}
      render={({field: {value, onChange}}) => (
        <LocalizationProvider adapterLocale={ru} dateAdapter={AdapterDateFns}>
          <DatePicker
            value={value || null}
            onChange={onChange}
            label="Дата окончания"
            format="dd.MM.yyyy"
            slotProps={{popper: {disablePortal: true}}}
          />
        </LocalizationProvider>
      )}
    />
  ),
} as const;

export const ContractsTableCreateFilterFieldBuilder: FC = () => {
  const {control, watch} = useFormContext<ContractFiltersFormValues>();
  const {data = []} = useGetDictsServiceTypesMyQuery({});
  const criteriaValue = watch('criteria');
  const render = get(FIELD_RENDERER, criteriaValue);

  const fieldOptions = useMemo(
    () => ({
      status: CONTRACT_TABLE_FILTERS_STATUSES,
      serviceType: data?.map((type) => ({value: type.code, label: type.name})),
    }),
    [data],
  );

  if (!render) return null;

  return render({
    control,
    options: [...get(fieldOptions, criteriaValue, [])],
  });
};
